/**
 * <bigens-set-tray> (T41b): the "Your set" tray, shared by the PDP deal builder and the mix & match page (T42).
 *
 * A view only. The owner calls `tray.update(state)` whenever the set changes and listens for the intents the tray
 * emits (bubbling CustomEvents):
 *   - `bigens-set-tray:add`                     "+ Add another ball" or an empty slot
 *   - `bigens-set-tray:change` { index }        a filled slot (index ≥ 1; ball 1 follows the page's own picker)
 *   - `bigens-set-tray:remove` { index }        the × on a filled slot
 *
 * state = {
 *   balls: Array<{ family, name, size, color, hex, img, preorder, offered, fixed } | null>,  one per ball in the set
 *   tiers: number[],   percent per count (index = count − 1; the last one stands for 4+)
 *   max:   number,     the most balls a set can hold
 *   nudge: { price?: string, percent?: number } | null
 *   add:   { label: string, hint: string, link: boolean } | null   (T45: relabel the add button, show the mix link)
 *   each:  Record<number, string> | null   (T52: price per ball at 2, 3 and 4 balls, formatted; empty slots show it)
 * }
 *
 * Storage (shared with T42): `BigensSetTray.store` keeps the last set in localStorage under `bigens:set`:
 *   { version: 1, source: 'pdp' | 'page', updatedAt: ISO string, lines: [{ productId, variantId }] }
 *
 * Every string comes from the snippet's `data-tray-strings` JSON (locales `bigens.deal_builder.*`).
 */

const STORAGE_KEY = 'bigens:set';
const STORAGE_VERSION = 1;
const SLOTS_IN_A_ROW = 4;

const store = {
  /** @returns {{ version: number, source: string, updatedAt: string, lines: Array<{ productId: string, variantId: string }> } | null} */
  read() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || data.version !== STORAGE_VERSION || !Array.isArray(data.lines)) return null;
      return data;
    } catch {
      return null;
    }
  },

  /**
   * @param {Array<{ productId: string, variantId: string }>} lines
   * @param {string} source
   */
  write(lines, source = 'pdp') {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ version: STORAGE_VERSION, source, updatedAt: new Date().toISOString(), lines }),
      );
    } catch {
      /* private mode or storage full: the set still works on the page */
    }
  },

  clear() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  },
};

class BigensSetTray extends HTMLElement {
  static store = store;

  /** @type {Record<string, string>} */
  strings = {};

  connectedCallback() {
    try {
      this.strings = JSON.parse(this.querySelector('[data-tray-strings]')?.textContent || '{}');
    } catch {
      this.strings = {};
    }
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;

    if (button.matches('[data-tray-add]')) {
      this.#emit('bigens-set-tray:add');
    } else if (button.matches('[data-tray-change]')) {
      this.#emit('bigens-set-tray:change', { index: Number(button.dataset.trayChange) });
    } else if (button.matches('[data-tray-remove]')) {
      this.#emit('bigens-set-tray:remove', { index: Number(button.dataset.trayRemove) });
    }
  };

  /**
   * @param {string} name
   * @param {Record<string, unknown>} [detail]
   */
  #emit(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  /**
   * @param {{ balls: Array<any>, tiers: number[], max: number, nudge: { price?: string, percent?: number } | null }} state
   */
  update({ balls = [], tiers = [0, 0, 0, 0], max = 4, nudge = null, add = null, each = null }) {
    const count = balls.length;
    const slots = this.querySelector('[data-tray-slots]');
    if (slots) {
      const nodes = balls.map((ball, index) => this.#filledSlot(ball, index));
      // Empty slots up to a full row of four, each showing the saving it unlocks; beyond four the add button does the job.
      for (let index = count; index < SLOTS_IN_A_ROW && index < max; index += 1) {
        nodes.push(this.#emptySlot(index, tiers, each));
      }
      slots.replaceChildren(...nodes);
    }

    const countNode = this.querySelector('[data-tray-count]');
    if (countNode) {
      countNode.textContent = ` · ${count === 1 ? this.strings.countOne : this.text('countOther', { n: count })}`;
    }

    // Tier bar: the fill reaches the active mark, marks up to it light up.
    const activeTier = Math.max(1, Math.min(count, SLOTS_IN_A_ROW));
    const tier = this.querySelector('[data-tray-tier]');
    if (tier instanceof HTMLElement) {
      tier.style.setProperty('--set-progress', String((activeTier - 1) / (SLOTS_IN_A_ROW - 1)));
    }
    this.querySelectorAll('[data-tray-mark]').forEach((mark) => {
      const value = Number(mark.getAttribute('data-tray-mark'));
      mark.classList.toggle('is-reached', value <= activeTier);
      mark.classList.toggle('is-active', value === activeTier);
      if (value === activeTier) mark.setAttribute('aria-current', 'true');
      else mark.removeAttribute('aria-current');
    });

    const nudgeNode = this.querySelector('[data-tray-nudge]');
    const nudgeText = this.querySelector('[data-tray-nudge-text]');
    if (nudgeNode instanceof HTMLElement && nudgeText) {
      const text = nudge?.price
        ? this.text('nudgePrice', { price: nudge.price })
        : nudge?.percent
          ? this.text('nudgePercent', { percent: nudge.percent })
          : '';
      nudgeText.textContent = text;
      nudgeNode.hidden = text === '';
    }

    const addButton = this.querySelector('.bigens-set-tray__add[data-tray-add]');
    if (addButton instanceof HTMLButtonElement) addButton.hidden = count >= max;

    // T45: the owner can relabel the add button ("Add another 50 in Green" when only one ball can be bought) and
    // show the "Build a mixed set" link.
    if (add) {
      const label = this.querySelector('.bigens-set-tray__add-label');
      const hint = this.querySelector('.bigens-set-tray__add-hint');
      if (label && add.label) label.textContent = add.label;
      if (hint instanceof HTMLElement) {
        hint.textContent = add.hint || '';
        hint.hidden = !add.hint;
      }
      const link = this.querySelector('[data-tray-mix-link]');
      if (link instanceof HTMLElement) link.hidden = !add.link || count >= max;
      // "Mix sizes, colors and families" isn't true while only one ball can be bought.
      const trayHint = this.querySelector('[data-tray-hint]');
      if (trayHint instanceof HTMLElement) trayHint.hidden = Boolean(add.link);
    }
  }

  /** @param {number} index */
  focusSlot(index) {
    const target =
      this.querySelector(`[data-tray-change="${index}"]`) ||
      this.querySelector(`[data-tray-slot="${index}"]`) ||
      this.querySelector('.bigens-set-tray__add[data-tray-add]');
    if (target instanceof HTMLElement) target.focus();
  }

  focusAdd() {
    const add = this.querySelector('.bigens-set-tray__add[data-tray-add]');
    if (add instanceof HTMLElement && !add.hidden) add.focus();
    else this.focusSlot(this.querySelectorAll('[data-tray-change]').length);
  }

  /**
   * @param {any} ball
   * @param {number} index
   */
  #filledSlot(ball, index) {
    const number = index + 1;
    if (!ball) {
      return el('li', { class: 'bigens-set-tray__slot is-filled', 'data-tray-slot': String(index), tabindex: '-1' });
    }
    const label = [ball.name, ball.size].filter(Boolean).join(' ');
    const meta = [ball.color, ball.preorder ? this.strings.preorder : '', ball.offered === false ? this.strings.soldOut : '']
      .filter(Boolean)
      .join(' · ');
    const description = [label, meta].filter(Boolean).join(', ');

    // T52 reference v2: the real ball photo in a circle on the neutral set; the colour disc only without a photo.
    const icon = ball.img
      ? el(
          'span',
          { class: 'bigens-set-tray__photo', 'aria-hidden': 'true' },
          el('img', {
            class: 'bigens-set-tray__image',
            src: ball.img,
            alt: '',
            width: '46',
            height: '46',
            loading: 'lazy',
          }),
        )
      : el('span', {
          class: 'bigens-set-ball bigens-set-tray__ball',
          'aria-hidden': 'true',
          style: `--ball-color: ${ball.hex || familyColor(ball.family)};`,
        });

    const text = [
      el('span', { class: 'bigens-set-tray__name' }, label),
      meta ? el('span', { class: 'bigens-set-tray__meta' }, meta) : '',
    ];

    const children = [el('span', { class: 'bigens-set-tray__family', 'aria-hidden': 'true' })];

    if (ball.fixed) {
      // Ball 1 follows the page's own picker: a static tile with a hint for screen readers.
      children.push(
        el(
          'span',
          { class: 'bigens-set-tray__pick', 'data-tray-slot': String(index), tabindex: '-1' },
          icon,
          ...text,
          el('span', { class: 'visually-hidden' }, ` ${this.strings.ballOne || ''}`),
        ),
      );
    } else {
      children.push(
        el(
          'button',
          {
            type: 'button',
            class: 'bigens-set-tray__pick',
            'data-tray-change': String(index),
            'aria-label': `${this.text('change', { n: number })}: ${description}`,
          },
          icon,
          ...text,
        ),
        el(
          'button',
          {
            type: 'button',
            class: 'bigens-set-tray__remove',
            'data-tray-remove': String(index),
            'aria-label': `${this.text('remove', { n: number })}: ${description}`,
          },
          el('span', { class: 'bigens-set-tray__remove-icon', 'aria-hidden': 'true' }),
        ),
      );
    }

    return el(
      'li',
      {
        class: `bigens-set-tray__slot is-filled${ball.offered === false ? ' is-unavailable' : ''}`,
        'data-family': ball.family || '',
      },
      ...children,
    );
  }

  /**
   * T52 reference v2: ghost balls (one outlined disc per ball the slot stands for), "n balls" and the price per ball
   * at that tier when the owner knows it, else the saving in percent.
   * @param {number} index
   * @param {number[]} tiers
   * @param {Record<number, string> | null} [each]
   */
  #emptySlot(index, tiers, each = null) {
    const count = index + 1;
    const percent = tiers[Math.min(index, tiers.length - 1)] || 0;
    const eachPrice = each && each[count] ? this.text('eachPrice', { price: each[count] }) : '';
    const ghosts = el('span', { class: 'bigens-set-tray__ghosts', 'aria-hidden': 'true' });
    for (let ghost = 0; ghost < count; ghost += 1) ghosts.append(el('i'));
    return el(
      'li',
      { class: 'bigens-set-tray__slot is-empty' },
      el(
        'button',
        // Pointer shortcut only: the keyboard path is the "+ Add another ball" button below (one tab stop, not four).
        { type: 'button', class: 'bigens-set-tray__empty', 'data-tray-add': '', tabindex: '-1', 'aria-label': this.strings.addAnother || '' },
        ghosts,
        el('span', { class: 'bigens-set-tray__empty-count' }, this.text('countOther', { n: count })),
        eachPrice
          ? el('span', { class: 'bigens-set-tray__empty-saving' }, eachPrice)
          : percent > 0
            ? el('span', { class: 'bigens-set-tray__empty-saving' }, this.text('savePercent', { percent }))
            : '',
      ),
    );
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [values] - replaces `[name]` placeholders
   */
  text(key, values = {}) {
    let text = String(this.strings[key] || '');
    Object.entries(values).forEach(([name, value]) => {
      text = text.replaceAll(`[${name}]`, String(value));
    });
    return text;
  }
}

/**
 * Tiny DOM builder (text is always set as text, never as HTML).
 * @param {string} tag
 * @param {Record<string, string | boolean>} attributes
 * @param {...(Node | string)} children
 */
function el(tag, attributes = {}, ...children) {
  const node = document.createElement(tag);
  Object.entries(attributes).forEach(([name, value]) => {
    if (value === false) return;
    node.setAttribute(name, value === true ? '' : String(value));
  });
  children.forEach((child) => {
    if (child !== '' && child != null) node.append(child);
  });
  return node;
}

/** @param {string} family */
function familyColor(family) {
  return family ? `var(--bigens-family-${family})` : 'var(--bigens-black)';
}

if (!customElements.get('bigens-set-tray')) {
  customElements.define('bigens-set-tray', BigensSetTray);
}
